interface Column {
  width: string; // e.g., "w-[12%]", "w-[20%]"
  label: string;
  hasTooltip?: boolean;
}

interface TableSkeletonProps {
  columns: Column[];
  rows?: number;
  className?: string;
}

/**
 * Reusable table skeleton component for loading states
 * Follows DRY principle and ensures consistent loading UX across tables
 */
export function TableSkeleton({
  columns,
  rows = 3,
  className = "",
}: TableSkeletonProps) {
  return (
    <div className={`scrollbar-thin overflow-x-auto ${className}`}>
      <table className="w-full table-fixed border-collapse text-sm">
        <thead>
          <tr className="text-left text-xs font-medium tracking-wide text-neutral-500 uppercase">
            {columns.map((column, index) => (
              <th key={index} className={`${column.width} px-4 py-3`}>
                <div className="flex items-center gap-1">
                  <div
                    className="h-3 animate-pulse rounded bg-gray-300"
                    style={{
                      width: `${Math.min(column.label.length * 8, 120)}px`,
                    }}
                  />
                  {column.hasTooltip && (
                    <div className="h-3 w-3 animate-pulse rounded-full bg-gray-300" />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(rows)].map((_, rowIndex) => (
            <TableRowSkeleton key={rowIndex} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Individual table row skeleton
 */
function TableRowSkeleton({ columns }: { columns: Column[] }) {
  return (
    <tr className="transition-colors hover:bg-neutral-100/80">
      {columns.map((_, index) => (
        <td key={index} className="px-4 py-1">
          <div className="h-4 w-full animate-pulse rounded bg-gray-300" />
        </td>
      ))}
    </tr>
  );
}

/**
 * Specialized skeleton for token distribution table
 */
export function TokenDistributionTableSkeleton() {
  const columns: Column[] = [
    { width: "w-[40%]", label: "Users", hasTooltip: true },
    { width: "w-[45%]", label: "Tokens", hasTooltip: true },
    { width: "w-[15%]", label: "Actions", hasTooltip: true },
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col items-start justify-between space-y-5 rounded-lg bg-white p-5">
      {/* Header skeleton */}
      <div className="flex w-full flex-row justify-between">
        <div className="flex w-full flex-row justify-between">
          <div className="w-full space-y-2">
            <div className="h-5 w-48 animate-pulse rounded bg-gray-300" />
            <div className="h-3 w-56 animate-pulse rounded bg-gray-300" />
          </div>
          <div className="flex flex-row gap-2">
            <div className="h-8 w-32 animate-pulse rounded bg-gray-300" />
            <div className="h-8 w-24 animate-pulse rounded bg-gray-300" />
          </div>
        </div>
      </div>

      {/* Table skeleton */}
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <TableSkeleton columns={columns} rows={4} />
      </div>
    </div>
  );
}

/**
 * Specialized skeleton for activity log table
 */
export function ActivityLogTableSkeleton() {
  const columns: Column[] = [
    { width: "w-[12%]", label: "Job ID" },
    { width: "w-[18%]", label: "User" },
    { width: "w-[15%]", label: "Token" },
    { width: "w-[20%]", label: "Endpoint" },
    { width: "w-[8%]", label: "Method" },
    { width: "w-[12%]", label: "Timestamp" },
    { width: "w-[15%]", label: "Status" },
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col space-y-5 rounded-lg bg-white p-5">
      {/* Header skeleton */}
      <div className="flex flex-row items-center justify-between">
        <div className="space-y-2">
          <div className="h-5 w-32 animate-pulse rounded bg-gray-300" />
          <div className="h-3 w-48 animate-pulse rounded bg-gray-300" />
        </div>
        <div className="flex gap-2">
          <div className="h-8 w-32 animate-pulse rounded bg-gray-300" />
          <div className="h-8 w-32 animate-pulse rounded bg-gray-300" />
        </div>
      </div>

      {/* Table skeleton */}
      <TableSkeleton columns={columns} rows={3} />

      {/* Pagination skeleton */}
      <div className="flex flex-row items-center justify-between">
        <div className="h-4 w-48 animate-pulse rounded bg-gray-300" />
        <div className="flex items-center gap-2">
          <div className="h-8 w-24 animate-pulse rounded bg-gray-300" />
          <div className="h-8 w-8 animate-pulse rounded bg-gray-300" />
          <div className="h-8 w-8 animate-pulse rounded bg-gray-300" />
        </div>
        <div className="h-8 w-32 animate-pulse rounded bg-gray-300" />
      </div>
    </div>
  );
}
